/**
 * Live Trading Feed Page
 * Real-time entry/exit signals with enhancement context
 */

import React, { useEffect, useState } from 'react';
import '../styles/validation.css';

interface FeedItem {
  timestamp: string;
  type: 'entry' | 'exit';
  symbol: string;
  price: number;
  size: number;
  enhancements: {
    correlation: string;
    regime: string;
    volatility_multiplier: number;
    ensemble_agreement: string;
  };
  status: string;
}

const API_BASE = 'https://trading-api-1075463475276.us-central1.run.app/api/trading-validation';

const LiveFeed: React.FC = () => {
  const [feed, setFeed] = useState<FeedItem[]>([]);
  const [filters, setFilters] = useState({ symbol: '', type: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeed = async () => {
      try {
        const res = await fetch(`${API_BASE}/live-feed?limit=100`);
        const data = await res.json();
        setFeed(data.data || []);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching feed:', error);
        setLoading(false);
      }
    };

    fetchFeed();

    // Poll every 2 minutes
    const interval = setInterval(fetchFeed, 120000);
    return () => clearInterval(interval);
  }, []);

  const filteredFeed = feed.filter(item => {
    if (filters.symbol && item.symbol !== filters.symbol.toUpperCase()) return false;
    if (filters.type && item.type !== filters.type) return false;
    return true;
  });

  return (
    <div className="validation-container">
      <div className="validation-header">
        <h1>Live Trading Feed</h1>
        <p className="subtitle">Real-time entry and exit signals</p>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <input
          type="text"
          placeholder="Filter by symbol (e.g., AAPL)"
          value={filters.symbol}
          onChange={(e) => setFilters({ ...filters, symbol: e.target.value })}
          className="filter-input"
        />
        <select
          value={filters.type}
          onChange={(e) => setFilters({ ...filters, type: e.target.value })}
          className="filter-select"
        >
          <option value="">All Types</option>
          <option value="entry">Entries Only</option>
          <option value="exit">Exits Only</option>
        </select>
        <div className="filter-count">
          Showing {filteredFeed.length} of {feed.length} trades
        </div>
      </div>

      {/* Feed Items */}
      {loading ? (
        <div className="loading">Loading feed...</div>
      ) : (
        <div className="feed-items-container">
          {filteredFeed.length > 0 ? (
            filteredFeed.map((item, idx) => (
              <div key={idx} className={`feed-item-detail ${item.type}`}>
                <div className="item-header">
                  <span className="item-time">{new Date(item.timestamp).toLocaleTimeString()}</span>
                  <span className={`item-type ${item.type}`}>{item.type.toUpperCase()}</span>
                  <span className="item-symbol">{item.symbol}</span>
                  <span className="item-price">${item.price.toFixed(2)}</span>
                  <span className="item-size">${item.size.toLocaleString()}</span>
                  <span className={`item-status ${item.status.toLowerCase()}`}>{item.status}</span>
                </div>

                <div className="item-details">
                  <div className="detail-row">
                    <span className="detail-label">Correlation Check:</span>
                    <span className="detail-value">{item.enhancements.correlation}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Market Regime:</span>
                    <span className="detail-value">{item.enhancements.regime}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Volatility Multiplier:</span>
                    <span className="detail-value">{item.enhancements.volatility_multiplier.toFixed(2)}x</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Ensemble Agreement:</span>
                    <span className="detail-value">{item.enhancements.ensemble_agreement}</span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="no-data">No trades match your filters</div>
          )}
        </div>
      )}
    </div>
  );
};

export default LiveFeed;
